import React from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearError,
  requestPasswordResetCode,
  resetPasswordWithCode,
} from '../../store/slices/authSlice';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{10}$/;

const ForgotPasswordModal = ({ show, onHide, role }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);
  const [identifier, setIdentifier] = React.useState('');
  const [otp, setOtp] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [codeSent, setCodeSent] = React.useState(false);
  const [resetComplete, setResetComplete] = React.useState(false);
  const [requestToken, setRequestToken] = React.useState('');
  const [maskedTarget, setMaskedTarget] = React.useState('');
  const [localError, setLocalError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const closeTimerRef = React.useRef(null);

  React.useEffect(() => {
    if (show) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      dispatch(clearError());
      setLocalError('');
      setMessage('');
    }
  }, [dispatch, show]);

  React.useEffect(() => () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
  }, []);

  const resetState = () => {
    setIdentifier('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setCodeSent(false);
    setResetComplete(false);
    setRequestToken('');
    setMaskedTarget('');
    setLocalError('');
    setMessage('');
    dispatch(clearError());
  };

  const handleClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    resetState();
    onHide();
  };

  const buildResetRequest = () => {
    const trimmedIdentifier = identifier.trim();
    const phone = trimmedIdentifier.replace(/\D/g, '');

    if (EMAIL_REGEX.test(trimmedIdentifier)) {
      return { email: trimmedIdentifier.toLowerCase() };
    }

    if (role !== 'driver' && PHONE_REGEX.test(phone)) {
      return { phone };
    }

    return null;
  };

  const handleSendCode = async () => {
    if (loading) return;
    const resetRequest = buildResetRequest();

    if (!resetRequest) {
      setLocalError('Enter a valid email address or 10-digit mobile number.');
      return;
    }

    setLocalError('');
    setMessage('');
    const result = await dispatch(requestPasswordResetCode(resetRequest));

    if (!result.error) {
      const token =
        result.payload?.requestToken ||
        result.payload?.otpToken ||
        result.payload?.request_token ||
        result.payload?.otp_token ||
        '';
      setCodeSent(true);
      setRequestToken(token);
      setMaskedTarget(result.payload?.maskedEmail || result.payload?.maskedPhone || '');
      setMessage(result.payload?.message || 'Reset OTP sent.');
    }
  };

  const handleResetPassword = async () => {
    if (!requestToken) {
      setLocalError('Request token missing. Please send the OTP again.');
      return;
    }

    if (loading) return;
    if (!/^\d{4,}$/.test(otp)) {
      setLocalError('Enter the OTP you received.');
      return;
    }

    if (newPassword.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }

    if (role !== 'driver' && newPassword !== confirmPassword) {
      setLocalError('Passwords must match.');
      return;
    }

    setLocalError('');
    setMessage('');
    const result = await dispatch(
      resetPasswordWithCode({
        ...buildResetRequest(),
        requestToken,
        otp: otp.trim(),
        newPassword,
      })
    );

    if (!result.error) {
      setMessage(result.payload?.message || 'Password reset successfully. You can log in now.');
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setRequestToken('');
      setMaskedTarget('');
      setCodeSent(false);
      setResetComplete(true);
      closeTimerRef.current = setTimeout(() => {
        handleClose();
      }, 1500);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered dialogClassName="auth-reset-modal">
      <Modal.Header closeButton>
        <Modal.Title>Reset password</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(localError || error) && <Alert variant="danger">{localError || error}</Alert>}
        {message && <Alert variant="success">{message}</Alert>}

        {!resetComplete && (
          <Form.Group className="mb-3">
            <Form.Label>{role === 'driver' ? 'Email' : 'Email or Mobile Number'}</Form.Label>
            <Form.Control
              type="text"
              value={identifier}
              onChange={(event) => {
                setIdentifier(event.target.value);
                setLocalError('');
              }}
              placeholder="Enter email or 10-digit mobile"
              disabled={loading || codeSent}
            />
          </Form.Group>
        )}

        {codeSent && !resetComplete && (
          <>
            {maskedTarget && (
              <Alert variant="info" className="py-2">
                OTP sent to {maskedTarget}.
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>OTP</Form.Label>
              <Form.Control
                value={otp}
                onChange={(event) => {
                  setOtp(event.target.value.replace(/\D/g, '').slice(0, 12));
                  setLocalError('');
                }}
                placeholder="Enter OTP"
                disabled={loading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                  setLocalError('');
                }}
                placeholder="Enter new password"
                disabled={loading}
              />
            </Form.Group>

            {role !== 'driver' && <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  setLocalError('');
                }}
                placeholder="Confirm new password"
                disabled={loading}
              />
            </Form.Group>}}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
          {resetComplete ? 'Back to Login' : 'Close'}
        </Button>
        {!resetComplete && (
          <Button variant="primary" onClick={codeSent ? handleResetPassword : handleSendCode} disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Working...
              </>
            ) : codeSent ? 'Reset Password' : 'Send Code'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ForgotPasswordModal;
